import React from 'react';
import { X, ChefHat, Briefcase, Send, CheckCircle2 } from 'lucide-react';
import { adminApi } from '../../lib/api';

/** Modale superadmin "Envoyer un formulaire d'inscription" (à côté de "Ajouter un client" dans
 *  AdminClientsView) : choix du type de client puis email, envoie un lien public à usage unique
 *  (cf. ClientRegistrationForm) que le futur client remplit lui-même. */
export const SendRegistrationModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [step, setStep] = React.useState<1 | 2>(1);
    const [clientType, setClientType] = React.useState<'restauration' | 'prestation'>('restauration');
    const [email, setEmail] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [sent, setSent] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleSend = async () => {
        if (!email.trim()) { setError('Merci de renseigner un email.'); return; }
        setSending(true);
        setError('');
        try {
            const result: any = await adminApi.sendRegistrationInvite({ client_type: clientType, email: email.trim() });
            if (result?.emailSent === false) {
                setError(result.emailError || "L'email n'a pas pu être envoyé.");
            } else {
                setSent(true);
            }
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-surface border border-border rounded-2xl p-8 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-content">Envoyer un formulaire d'inscription</h2>
                    <button onClick={onClose} className="text-content-muted hover:text-content"><X size={20} /></button>
                </div>

                {sent ? (
                    <div className="text-center py-6 space-y-4">
                        <div className="flex justify-center"><CheckCircle2 className="text-green-500" size={40} /></div>
                        <p className="text-content font-semibold">Invitation envoyée à {email}</p>
                        <button onClick={onClose} className="px-4 py-2 rounded-lg bg-surface-soft text-content font-bold hover:bg-content hover:text-surface transition-all">Fermer</button>
                    </div>
                ) : step === 1 ? (
                    <div className="space-y-4">
                        <p className="text-sm text-content-muted">Quel type de client souhaitez-vous inviter ?</p>
                        <div className="grid grid-cols-2 gap-3">
                            <button type="button" onClick={() => setClientType('restauration')}
                                className={`p-4 rounded-xl border text-left transition-all ${clientType === 'restauration' ? 'border-brand-500 bg-brand-500/10' : 'border-border bg-surface-soft'}`}>
                                <ChefHat className={clientType === 'restauration' ? 'text-brand-500' : 'text-content-muted'} size={22} />
                                <p className="text-sm font-bold text-content mt-2">Restauration</p>
                                <p className="text-xs text-content-muted">Restauration / Hôtellerie</p>
                            </button>
                            <button type="button" onClick={() => setClientType('prestation')}
                                className={`p-4 rounded-xl border text-left transition-all ${clientType === 'prestation' ? 'border-brand-500 bg-brand-500/10' : 'border-border bg-surface-soft'}`}>
                                <Briefcase className={clientType === 'prestation' ? 'text-brand-500' : 'text-content-muted'} size={22} />
                                <p className="text-sm font-bold text-content mt-2">Prestation</p>
                                <p className="text-xs text-content-muted">Prestation de service</p>
                            </button>
                        </div>
                        <button onClick={() => setStep(2)} className="w-full py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold">Suivant</button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-500 text-sm rounded-lg px-3 py-2">{error}</div>}
                        <div>
                            <label className="block text-xs font-bold text-content-muted uppercase mb-1">Email du futur client</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm outline-none focus:border-brand-500"
                                placeholder="contact@etablissement.fr" autoFocus />
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setStep(1)} className="flex-1 py-2.5 rounded-xl border border-border text-content font-bold hover:bg-surface-soft">Retour</button>
                            <button onClick={handleSend} disabled={sending}
                                className="flex-1 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
                                <Send size={14} /> {sending ? 'Envoi...' : 'Envoyer'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
